import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollText, Search, Download, Eye } from "lucide-react";

const CAT_LABELS = { auth: "Autenticazione", tenant: "Azienda", users: "Utenti", apikeys: "API Key", settings: "Impostazioni", integrations: "Integrazioni", superadmin: "Super Admin", contacts: "Contatti", campaigns: "Campagne", automations: "Automazioni" };

export default function AuditLogs() {
  const [filters, setFilters] = useState({ search: "", category: "all", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const { data, isLoading } = useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: async () => {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      return (await api.get("/audit-logs", { params })).data.data;
    },
  });

  const exportCsv = () => {
    const token = localStorage.getItem("cx_access_token");
    const params = new URLSearchParams();
    if (filters.category !== "all") params.set("category", filters.category);
    if (filters.search) params.set("search", filters.search);
    fetch(`${API}/audit-logs/export?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "audit_logs.csv"; a.click();
      });
  };

  return (
    <div>
      <PageHeader title="Audit log" subtitle="Traccia completa delle azioni nel tuo tenant" icon={ScrollText}
        actions={<Button variant="outline" className="rounded-lg" onClick={exportCsv} data-testid="export-csv-btn"><Download className="mr-1 h-4 w-4" /> Esporta CSV</Button>} />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca..." value={filters.search} onChange={(e) => set("search", e.target.value)} className="pl-9" data-testid="audit-search" />
        </div>
        <Select value={filters.category} onValueChange={(v) => set("category", v)}>
          <SelectTrigger data-testid="audit-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {Object.entries(CAT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="audit-from" />
        <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="audit-to" />
      </div>

      <Card className="overflow-hidden" data-testid="audit-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Azione</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="text-right">Dettaglio</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>) : (
                data?.logs.length === 0 ? <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nessun evento trovato</TableCell></TableRow> :
                data?.logs.map((l) => (
                  <TableRow key={l.id} data-testid="audit-row">
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</TableCell>
                    <TableCell className="text-sm">{l.user_name || "—"}</TableCell>
                    <TableCell><code className="rounded bg-muted px-1.5 py-0.5 text-xs">{l.action}</code></TableCell>
                    <TableCell><Badge variant="secondary" className="font-normal">{CAT_LABELS[l.category] || l.category}</Badge></TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{l.description}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => setDetail(l)}><Eye className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {data && <div className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">Totale: {data.total} eventi</div>}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="audit-detail">
          <DialogHeader><DialogTitle>Dettaglio evento</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <Row label="Azione" value={detail.action} />
              <Row label="Categoria" value={CAT_LABELS[detail.category] || detail.category} />
              <Row label="Descrizione" value={detail.description} />
              <Row label="Utente" value={detail.user_name} />
              <Row label="Data" value={new Date(detail.created_at).toLocaleString("it-IT")} />
              <Row label="IP" value={detail.ip || "—"} />
              <Row label="User Agent" value={detail.user_agent || "—"} />
              {detail.metadata && Object.keys(detail.metadata).length > 0 && (
                <div><p className="text-muted-foreground">Metadati</p><pre className="mt-1 overflow-x-auto rounded-lg bg-muted p-3 text-xs">{JSON.stringify(detail.metadata, null, 2)}</pre></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between gap-4 border-b border-border/50 py-1.5"><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{value}</span></div>;
}
